import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { X, Gem, Zap, Crown, Box, ArrowUpRight, Clock } from 'lucide-react'
import { parseSkinName, formatPrice } from '../utils/formatters'
import { formatAgo, getSourceLabel } from '../features/profile/utils/profileFormat'
import useUiOverlayStore from '../store/useUiOverlayStore'
import { LOGO_SRC } from '../config/brand'

// Цвет редкости скина (color из API, "#rrggbb" или "rrggbb") → rgba-строка
const rarityAlpha = (color, alpha) => {
  const hex = typeof color === 'string' ? color.replace('#', '') : ''
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return 'transparent'
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Фуллскрин-презентация выбитого скина «для скриншота» (виральная витрина).
 * Единый золотистый брендовый стиль для всех пользователей — кастомные цвета
 * профиля НЕ применяются. Подходит и для профиля, и для страницы инвентаря:
 * любой из параметров item может отсутствовать (multiplier/source/received_ago
 * есть только у top_skins профиля).
 *
 * @param {boolean} isOpen - флаг открытия
 * @param {function} onClose - закрытие (клик по фону, крестик, Esc)
 * @param {object} item - { name, img, color, cost, multiplier?, source?, received_ago?, cost_currency? }
 * @param {number} rank - место в топе (опционально, бейдж «Топ #N»)
 * @param {string} owner - имя владельца (опционально, строка «Выбил …»)
 */
const SkinShowcaseModal = ({ isOpen, onClose, item, rank, owner }) => {
  const { t } = useTranslation()

  // Пока модалка открыта: блокируем скролл страницы, прячем кнопку «наверх»
  // (useUiOverlayStore) и закрываемся по Esc
  useEffect(() => {
    if (!isOpen) return undefined
    const { pushOverlay, popOverlay } = useUiOverlayStore.getState()
    pushOverlay()
    document.body.style.overflow = 'hidden'
    const handleKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKey)
    return () => {
      popOverlay()
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handleKey)
    }
  }, [isOpen, onClose])

  if (!isOpen || !item) return null

  const skin = parseSkinName(item.name)
  const hasStats = item.multiplier != null || item.source || item.received_ago != null || item.cost_currency

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[70] flex overflow-y-auto p-5"
      style={{
        background:
          'radial-gradient(90% 55% at 50% -5%, rgba(250, 204, 21, 0.16), transparent 65%), ' +
          'radial-gradient(70% 45% at 50% 105%, rgba(0, 255, 127, 0.08), transparent 65%), ' +
          'rgba(5, 5, 10, 0.97)'
      }}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.92, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="m-auto w-full max-w-sm"
      >
        {/* Золотая рамка-витрина */}
        <div className="rounded-[28px] bg-gradient-to-b from-yellow-300/80 via-amber-500/30 to-white/10 p-px shadow-[0_30px_80px_-20px_rgba(250,204,21,0.35)]">
          <div className="relative overflow-hidden rounded-[27px] bg-[#0C0C12] px-6 pb-7 pt-6">
            {/* Мягкое свечение цвета редкости под скином */}
            <div
              className="pointer-events-none absolute left-1/2 top-1/3 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
              style={{ backgroundColor: rarityAlpha(item.color, 0.3) }}
            />
            {/* Лучи сверху от рамки */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[radial-gradient(80%_100%_at_50%_0%,rgba(250,204,21,0.15),transparent_70%)]" />

            <button
              onClick={onClose}
              className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition-all active:scale-95"
              aria-label={t('common.close', 'Закрыть')}
            >
              <X size={15} />
            </button>

            {/* Логотип бренда */}
            <div className="relative mx-auto w-28">
              <img
                src={LOGO_SRC}
                alt="SKINOZ"
                className="relative z-10 w-full"
              />
              <div className="pointer-events-none absolute left-1/2 top-1/2 h-6 w-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent-primary/25 blur-xl" />
            </div>

            {/* Бейдж места в топе */}
            {rank != null && (
              <div className="relative mt-3 flex justify-center">
                <span className="flex items-center gap-1 rounded-full bg-gradient-to-r from-yellow-300 to-orange-500 px-3 py-1 text-[11px] font-black italic uppercase tracking-wide text-dark-primary tabular-nums">
                  <Crown size={11} />
                  {t('skinShowcase.topRank', { rank, defaultValue: 'Топ #{{rank}}' })}
                </span>
              </div>
            )}

            {/* Скин */}
            <div className="relative mt-5 flex h-52 items-center justify-center">
              {item.img ? (
                <img
                  src={item.img}
                  alt={item.name}
                  className="max-h-full max-w-full object-contain drop-shadow-[0_20px_30px_rgba(0,0,0,0.6)]"
                  onError={(e) => { e.target.style.display = 'none' }}
                />
              ) : null}
            </div>

            {/* Название */}
            <h3 className="relative mt-4 text-center text-lg font-black italic uppercase leading-tight text-white">
              {skin.title}
            </h3>
            {(skin.subtitleFull || skin.subtitleCompact) && (
              <div className="relative mt-1 text-center text-xs uppercase tracking-wider text-text-secondary">
                {skin.subtitleFull || skin.subtitleCompact}
              </div>
            )}

            {/* Цена — брендовый зелёный градиент, одинаковая для всех */}
            <div className="relative mt-3 flex items-center justify-center gap-1.5">
              <Gem size={18} className="text-accent-primary" />
              <span className="bg-gradient-to-r from-accent-primary to-accent-secondary bg-clip-text text-2xl font-bold tabular-nums text-transparent">
                {formatPrice(item.cost)}
              </span>
            </div>

            {/* Чипы: множитель, источник, когда получено */}
            {hasStats && (
              <div className="relative mt-4 flex flex-wrap items-center justify-center gap-1.5">
                {item.multiplier != null && (
                  <span className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-bold tabular-nums text-accent-primary">
                    <Zap size={10} />
                    ×{item.multiplier}
                  </span>
                )}
                {item.cost_currency && (
                  <span className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium tabular-nums text-white/80">
                    ≈ {item.cost_currency}
                  </span>
                )}
                {item.source && (
                  <span className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-white/80">
                    {item.source === 'upgrader' ? <ArrowUpRight size={10} /> : <Box size={10} />}
                    {getSourceLabel(item.source, t)}
                  </span>
                )}
                {item.received_ago != null && (
                  <span className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-text-secondary">
                    <Clock size={10} />
                    {formatAgo(item.received_ago, t)}
                  </span>
                )}
              </div>
            )}

            {/* Владелец выигрыша */}
            {owner && (
              <div className="relative mt-5 border-t border-white/5 pt-3 text-center text-xs text-text-secondary">
                {t('skinShowcase.wonBy', { name: owner, defaultValue: 'Выбил {{name}}' })}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default SkinShowcaseModal
